import React, { useState, useEffect } from 'react';
import Modal, { ModalDirectionOptions } from '../../components/Modal';
import { useResusContext } from '../../Resus/ResusContext';
import {
  calcDilutionPerKg,
  getTargetVolumePerHour,
  formatNumber,
  getTimeUnitString
} from '../../Resus/medicationUtils';
import { Drip, AdministrationDetails } from '../types';
import { useTranslation } from 'react-i18next';

interface DripConfirmationDialogProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  drip: Drip;
  onConfirm: (dosage?: AdministrationDetails) => void;
}

const DripConfirmationDialog: React.FC<DripConfirmationDialogProps> = ({
  isOpen,
  setIsOpen,
  drip,
  onConfirm,
}) => {
  const { weight } = useResusContext();
  const { t } = useTranslation(['resus']);
  const defaultMlPerHour = weight ? getTargetVolumePerHour(drip) : 0;
  const [mlPerHour, setMlPerHour] = useState<string>(formatNumber(defaultMlPerHour));
  const [dosePerKg, setDosePerKg] = useState<string>(
    formatNumber(drip.dose_per_kg_per_hour || drip.dose_per_kg_per_min || 0)
  );
  const [dilutionVolume, setDilutionVolume] = useState<string>(
    formatNumber(drip.default_dilution_volume_ml || 0)
  );
  const initialDripDilutionInstruction = calcDilutionPerKg(drip, weight);

  useEffect(() => {
    if (isOpen) {
      setMlPerHour(formatNumber(defaultMlPerHour));
      setDosePerKg(formatNumber(drip.dose_per_kg_per_hour || drip.dose_per_kg_per_min || 0));
      setDilutionVolume(formatNumber(drip.default_dilution_volume_ml || 0));
    }
  }, [isOpen, defaultMlPerHour, drip]);

  const formatInputValue = (value: string): string => {
    if (value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return formatNumber(numValue);
  };

  const handleMlPerHourChange = (value: string) => {
    setMlPerHour(value);
  };

  const handleDosePerKgChange = (value: string) => {
    setDosePerKg(value);
  };

  const handleDilutionVolumeChange = (value: string) => {
    setDilutionVolume(value);
  };

  const handleConfirm = () => {
    onConfirm(createAdministartionDetails());
    setIsOpen(false);
  };

  const createAdministartionDetails = (): AdministrationDetails => {
    const modifiedDrip = {
      ...drip,
      target_volume_ml_per_hour: parseFloat(mlPerHour),
      default_dilution_volume_ml: parseFloat(dilutionVolume),
      ...(drip.dose_per_kg_per_hour
        ? { dose_per_kg_per_hour: parseFloat(dosePerKg) }
        : { dose_per_kg_per_min: parseFloat(dosePerKg) }
      )
    };

    const dripDilution = calcDilutionPerKg(modifiedDrip, weight);
    const dripLog = formatDripLog(modifiedDrip, dripDilution);
    return {
      medicalSectionLog: dripLog,
      resusLog: `${drip.name}: ${dripLog}`
    }
  };

  const formatDripLog = (drip: Drip, dripDilution: any): string => {
    const timeUnit = drip.dose_per_kg_per_min ? 'min' : 'hour';
    return `${dripDilution.doseForDilution} ${dripDilution.unitsForDilution} in ${drip.default_dilution_volume_ml} ml, ${formatInputValue(dosePerKg)} ${drip.dose_unit}/kg/${timeUnit}, ${formatInputValue(mlPerHour)} ml/hr`;
  };

  const dripDilution = calcDilutionPerKg({
    ...drip,
    target_volume_ml_per_hour: parseFloat(mlPerHour),
    default_dilution_volume_ml: parseFloat(dilutionVolume),
    ...(drip.dose_per_kg_per_hour
      ? { dose_per_kg_per_hour: parseFloat(dosePerKg) }
      : { dose_per_kg_per_min: parseFloat(dosePerKg) }
    )
  }, weight);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t('medicationDialogs.dripConfirmation.title')}
      direction={ModalDirectionOptions.RTL}
      primaryButton={{
        text: t('medicationDialogs.dripConfirmation.confirm'),
        onClick: handleConfirm,
      }}
      secondaryButton={{
        text: t('medicationDialogs.dripConfirmation.cancel'),
        onClick: () => setIsOpen(false),
      }}
    >
      <div style={{ textAlign: 'right', direction: 'rtl' }}>
        <div style={{ marginBottom: '20px' }}>
          {t('medicationDialogs.dripConfirmation.dilutionInBag', {
            dose: initialDripDilutionInstruction.doseForDilution,
            units: initialDripDilutionInstruction.unitsForDilution,
            volume: drip.default_dilution_volume_ml
          })}
          {t('medicationDialogs.dripConfirmation.changeAdministration')}
        </div>

        <div className="space-y-4">
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ width: '120px', alignSelf: 'center' }}>
              {t('medicationDialogs.dripConfirmation.dosePerKg', {
                unit: drip.dose_unit,
                timeUnit: getTimeUnitString(drip)
              })}
            </div>
            <input
              type="number"
              value={dosePerKg}
              onChange={(e) => handleDosePerKgChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              step="0.1"
              className="w-32 p-2 border border-gray-300 rounded"
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <div style={{ width: '120px', alignSelf: 'center' }}>
              {t('medicationDialogs.dripConfirmation.ratePerHour')}
            </div>
            <input
              type="number"
              value={mlPerHour}
              onChange={(e) => handleMlPerHourChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              step="0.5"
              className="w-32 p-2 border border-gray-300 rounded"
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <div style={{ width: '120px', alignSelf: 'center' }}>
              {t('medicationDialogs.dripConfirmation.bagSize')}
            </div>
            <input
              type="number"
              value={dilutionVolume}
              onChange={(e) => handleDilutionVolumeChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              step="5"
              className="w-32 p-2 border border-gray-300 rounded"
            />
          </div>
        </div>

        <div style={{ marginTop: '20px' }}>
          {t('medicationDialogs.dripConfirmation.requiredDilution', {
            dose: dripDilution.doseForDilution,
            units: dripDilution.unitsForDilution,
            volume: dilutionVolume
          })}
        </div>
      </div>
    </Modal>
  );
};

export default DripConfirmationDialog;